import { useImperativeHandle, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  setAnimating,
  goToQuestion,
  setShowBankControl,
  setBankOffsetY,
} from "@educaplay/store/slices/gameSlice";
import { assetsUrl } from "@educaplay/core";
import classes from "./Game.module.scss";
import PhaserGame from "./PhaserGame";

export default function Game({ onAnswer, ref }) {
  const dispatch = useDispatch();
  const phaserGameRef = useRef(null);

  const questions = useSelector((state) => state.game.questions);
  const randomized = useSelector((state) => state.game.randomized);
  const started = useSelector((state) => state.game.view !== "MAIN_MENU");

  // Handle animating state changes from Phaser
  const handleAnimatingChange = useCallback(
    (isAnimating) => {
      dispatch(setAnimating(isAnimating));
    },
    [dispatch],
  );

  // Sync store's currentIndex when Phaser starts a new question
  const handleQuestionStart = useCallback(
    (storeIndex) => {
      dispatch(goToQuestion(storeIndex));
    },
    [dispatch],
  );

  const handleBankControl = useCallback(
    ({ visible = true, offsetY = 0 } = {}) => {
      dispatch(setShowBankControl(visible));
      dispatch(setBankOffsetY(offsetY));
    },
    [dispatch],
  );

  // Handle answer from Phaser
  const handleAnswer = useCallback(
    (answer, options = {}) => {
      // Sync Redux's currentIndex to Phaser's view BEFORE answerQuestion runs.
      // This ensures answerQuestion always reads the correct currentIndex
      // (e.g. for tries.push), regardless of any prior drift.
      if (options.questionIndexAnswer !== undefined) {
        dispatch(goToQuestion(options.questionIndexAnswer));
      }
      if (onAnswer) {
        onAnswer(answer, {
          showFeedback: false,
          playErrorSound: false,
          ...options,
        });
      }
    },
    [onAnswer, dispatch],
  );

  useImperativeHandle(
    ref,
    () => ({
      animateEntry: async () => {
        // Entry animation is handled by Phaser internally
        return Promise.resolve();
      },
      animateFail: async () => {
        // Fail animation triggered from React (e.g., timeout)
        if (phaserGameRef.current) {
          phaserGameRef.current.triggerTimeOut();
        }
        return Promise.resolve();
      },
      animateExit: async () => {
        // Signal game-over to Phaser and resolve quickly.
        // Avoid blocking SCORE_SCREEN on long Phaser animations.
        return new Promise((resolve) => {
          let settled = false;

          const finish = () => {
            if (settled) return;
            settled = true;
            resolve();
          };

          const fallback = setTimeout(() => {
            finish();
          }, 1000);

          if (phaserGameRef.current?.setGameOver) {
            const scene = phaserGameRef.current.getScene?.();

            // If scene is already finishing, don't wait for callbacks.
            if (scene?.finish) {
              clearTimeout(fallback);
              finish();
              return;
            }

            phaserGameRef.current.setGameOver(() => {
              clearTimeout(fallback);
              finish();
            });
          } else {
            clearTimeout(fallback);
            finish();
          }
        });
      },
    }),
    [],
  );

  return (
    <div className={classes.gameContainer}>
      <div className={classes.phaserCanvas}>
        <PhaserGame
          ref={phaserGameRef}
          questions={questions}
          onAnswer={handleAnswer}
          onAnimatingChange={handleAnimatingChange}
          onQuestionStart={handleQuestionStart}
          onBankControl={handleBankControl}
          assetsUrl={assetsUrl}
          soundEnabled={true}
          randomizeQuestions={randomized}
          randomizeAnswers={false}
          started={started}
        />
      </div>
    </div>
  );
}
